"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.getProjectTypeByCapability = exports.scaffoldQuestionForVSCode = exports.folderAndAppNameCondition = exports.languageNode = exports.getDefaultLanguage = exports.getLanguageOptions = exports.LanguageOptionMap = void 0;
const featureFlags_1 = require("../../../common/featureFlags");
const localizeUtils_1 = require("../../../common/localizeUtils");
const metadata_1 = require("../../../component/generator/templates/metadata");
const constants_1 = require("../../constants");
const create_1 = require("../../create");
const CapabilityOptions_1 = require("./CapabilityOptions");
const ProjectTypeOptions_1 = require("./ProjectTypeOptions");
const customAgentProjectTypeNode_1 = require("./customAgentProjectTypeNode");
const daProjectTypeNode_1 = require("./daProjectTypeNode");
const officeAddinProjectTypeNode_1 = require("./officeAddinProjectTypeNode");
const teamsProjectTypeNode_1 = require("./teamsProjectTypeNode");
exports.LanguageOptionMap = new Map([
    [constants_1.ProgrammingLanguage.JS, { id: constants_1.ProgrammingLanguage.JS, label: "JavaScript" }],
    [constants_1.ProgrammingLanguage.TS, { id: constants_1.ProgrammingLanguage.TS, label: "TypeScript" }],
    [constants_1.ProgrammingLanguage.CSharp, { id: constants_1.ProgrammingLanguage.CSharp, label: "C#" }],
    [constants_1.ProgrammingLanguage.PY, { id: constants_1.ProgrammingLanguage.PY, label: "Python" }],
    [constants_1.ProgrammingLanguage.Common, { id: constants_1.ProgrammingLanguage.Common, label: "None" }],
    [constants_1.ProgrammingLanguage.None, { id: constants_1.ProgrammingLanguage.None, label: "None" }],
]);
function getLanguageOptions(inputs) {
    const templateName = inputs[constants_1.QuestionNames.TemplateName];
    const languages = (0, metadata_1.getAllTemplatesOnPlatform)(inputs.platform)
        .filter((t) => t.name === templateName)
        .map((t) => t.language)
        .filter((lang) => lang !== undefined);
    const languageOptions = languages.map((lang) => exports.LanguageOptionMap.get(lang) || {
        id: constants_1.ProgrammingLanguage.Common,
        label: "None",
    });
    return languageOptions;
}
exports.getLanguageOptions = getLanguageOptions;
function getDefaultLanguage(inputs) {
    var _a;
    const options = getLanguageOptions(inputs);
    return (_a = options[0]) === null || _a === void 0 ? void 0 : _a.id;
}
exports.getDefaultLanguage = getDefaultLanguage;
function languageNode() {
    return {
        condition: (inputs) => {
            const templateName = inputs[constants_1.QuestionNames.TemplateName];
            const languages = (0, metadata_1.getAllTemplatesOnPlatform)(inputs.platform)
                .filter((t) => t.name === templateName)
                .map((t) => t.language)
                .filter((lang) => lang !== "none" && lang !== undefined);
            return languages.length > 0;
        },
        data: {
            type: "singleSelect",
            title: (0, localizeUtils_1.getLocalizedString)("core.ProgrammingLanguageQuestion.title"),
            name: constants_1.QuestionNames.ProgrammingLanguage,
            staticOptions: [
                { id: constants_1.ProgrammingLanguage.JS, label: "JavaScript" },
                { id: constants_1.ProgrammingLanguage.TS, label: "TypeScript" },
                { id: constants_1.ProgrammingLanguage.CSharp, label: "C#" },
                { id: constants_1.ProgrammingLanguage.PY, label: "Python" },
            ],
            dynamicOptions: getLanguageOptions,
            default: getDefaultLanguage,
            skipSingleOption: true,
        },
    };
}
exports.languageNode = languageNode;
function folderAndAppNameCondition(inputs) {
    // skip this project when need to redirect to Kiota: 1. Feature flag enabled 2. Creating plugin/declarative copilot from existing spec 3. No plugin manifest path
    // or start with github copilot
    return (!(featureFlags_1.featureFlagManager.getBooleanValue(featureFlags_1.FeatureFlags.KiotaIntegration) &&
        inputs[constants_1.QuestionNames.ActionType] === CapabilityOptions_1.ActionStartOptions.apiSpec().id &&
        (inputs[constants_1.QuestionNames.ProjectType] === ProjectTypeOptions_1.ProjectTypeOptions.copilotAgentOptionId ||
            inputs[constants_1.QuestionNames.Capabilities] === CapabilityOptions_1.DACapabilityOptions.declarativeAgent().id) &&
        !inputs[constants_1.QuestionNames.ActionManifestPath]) && inputs[constants_1.QuestionNames.ProjectType] !== ProjectTypeOptions_1.ProjectTypeOptions.startWithGithubCopilotOptionId);
}
exports.folderAndAppNameCondition = folderAndAppNameCondition;
/**
 * Scaffold question model dedicated for VS Code platform
 */
function scaffoldQuestionForVSCode() {
    const node = {
        data: { type: "group" },
        children: [
            {
                data: {
                    name: constants_1.QuestionNames.ProjectType,
                    title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.title"),
                    type: "singleSelect",
                    staticOptions: [
                        ProjectTypeOptions_1.ProjectTypeOptions.declarativeAgent(),
                        ProjectTypeOptions_1.ProjectTypeOptions.customEngineAgent(),
                        ProjectTypeOptions_1.ProjectTypeOptions.bot(),
                        ProjectTypeOptions_1.ProjectTypeOptions.tab(),
                        ProjectTypeOptions_1.ProjectTypeOptions.me(),
                        ProjectTypeOptions_1.ProjectTypeOptions.officeAddin(),
                        ...(featureFlags_1.featureFlagManager.getBooleanValue(featureFlags_1.FeatureFlags.ChatParticipantUIEntries)
                            ? [ProjectTypeOptions_1.ProjectTypeOptions.startWithGithubCopilot()]
                            : []),
                    ],
                },
                children: [
                    (0, daProjectTypeNode_1.daProjectTypeNode)(),
                    (0, customAgentProjectTypeNode_1.customEngineAgentProjectTypeNode)(),
                    (0, teamsProjectTypeNode_1.botProjectTypeNode)(),
                    (0, teamsProjectTypeNode_1.tabProjectTypeNode)(),
                    (0, teamsProjectTypeNode_1.meProjectTypeNode)(),
                    (0, officeAddinProjectTypeNode_1.officeAddinProjectTypeNode)(),
                ],
            },
            languageNode(),
            {
                condition: folderAndAppNameCondition,
                data: {
                    type: "group",
                },
                children: [
                    {
                        data: (0, create_1.folderQuestion)(),
                    },
                    {
                        data: (0, create_1.appNameQuestion)(),
                    },
                ],
            },
        ],
    };
    return node;
}
exports.scaffoldQuestionForVSCode = scaffoldQuestionForVSCode;
/**
 * CLI non-interactive mode has no "project-type" input, to make it compatible to the question model,
 * we need to convert capability to project type
 */
function getProjectTypeByCapability(capability) {
    if ([CapabilityOptions_1.DACapabilityOptions.declarativeAgent().id].includes(capability)) {
        return ProjectTypeOptions_1.ProjectTypeOptions.copilotAgentOptionId;
    }
    if ([
        CapabilityOptions_1.CustomCopilotCapabilityOptions.basicChatbot().id,
        CapabilityOptions_1.CustomCopilotCapabilityOptions.customCopilotRag().id,
        CapabilityOptions_1.CustomCopilotCapabilityOptions.aiAgent().id,
    ].includes(capability)) {
        return ProjectTypeOptions_1.ProjectTypeOptions.customCopilotOptionId;
    }
    if ([
        CapabilityOptions_1.BotCapabilityOptions.basicBot().id,
        CapabilityOptions_1.BotCapabilityOptions.notificationBot().id,
        CapabilityOptions_1.BotCapabilityOptions.commandBot().id,
        CapabilityOptions_1.BotCapabilityOptions.workflowBot().id,
    ].includes(capability)) {
        return ProjectTypeOptions_1.ProjectTypeOptions.botOptionId;
    }
    if ([
        CapabilityOptions_1.TabCapabilityOptions.nonSsoTab().id,
        CapabilityOptions_1.TabCapabilityOptions.m365SsoLaunchPage().id,
        CapabilityOptions_1.TabCapabilityOptions.dashboardTab().id,
        CapabilityOptions_1.TabCapabilityOptions.SPFxTab().id,
    ].includes(capability)) {
        return ProjectTypeOptions_1.ProjectTypeOptions.tabOptionId;
    }
    if ([
        CapabilityOptions_1.MeCapabilityOptions.m365SearchMe().id,
        CapabilityOptions_1.MeCapabilityOptions.collectFormMe().id,
        CapabilityOptions_1.MeCapabilityOptions.linkUnfurling().id,
    ].includes(capability)) {
        return ProjectTypeOptions_1.ProjectTypeOptions.meOptionId;
    }
    if ([
        CapabilityOptions_1.OfficeAddinCapabilityOptions.wxpTaskPane().id,
        CapabilityOptions_1.OfficeAddinCapabilityOptions.officeAddinImport().id,
    ].includes(capability)) {
        return ProjectTypeOptions_1.ProjectTypeOptions.officeMetaOSOptionId;
    }
    if ([
        CapabilityOptions_1.OfficeAddinCapabilityOptions.outlookTaskPane().id,
        CapabilityOptions_1.OfficeAddinCapabilityOptions.outlookAddinImport().id,
    ].includes(capability)) {
        return ProjectTypeOptions_1.ProjectTypeOptions.outlookAddinOptionId;
    }
    return "";
}
exports.getProjectTypeByCapability = getProjectTypeByCapability;
//# sourceMappingURL=createRootNode.js.map